import { Resources } from 'constants/resources';

import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRecordModalById } from 'hooks/useRecordModalById';
import { GatewayRuleField } from 'pages/GatewayRules/components/GatewayRuleField';
import { GatewayRuleMappingPercentEditDialog } from 'pages/GatewayRules/components/GatewayRuleMappingPercentEditDialog/GatewayRuleMappingPercentEditDialog';
import { GatewayRuleModalDelete } from 'pages/GatewayRules/components/GatewayRuleModalDelete/GatewayRuleModalDelete';
import { GatewayRuleConditionsProvider } from 'pages/GatewayRules/context/GatewayRuleConditionsContext';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useShowController } from 'react-admin';
import { Link } from 'react-router-dom';
import { Box } from 'shared/mui/Box';
import { Button, DeleteButton } from 'shared/mui/Buttons';
import { CardHeader } from 'shared/mui/Card';
import { UserActionsIconButton } from 'shared/mui/IconButtons/UserActionsIconButton/UserActionsIconButton';
import { LinearProgress } from 'shared/mui/LinearProgress/LinearProgress';
import { ShowPage } from 'shared/react-admin/Pages/ShowPage/ShowPage';
import { CommissionShowDialog } from 'shared/widgets/CommissionShowDialog/CommissionShowDialog';
import { GatewayRuleActiveSwitch } from 'shared/widgets/GatewayRuleActiveSwitch/GatewayRuleActiveSwitch';
import { GatewayShowDialog } from 'shared/widgets/GatewayShowDialog/GatewayShowDialog';
import { MerchantAccountShowDialog } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialog';
import { Commission } from 'types/embedded/commission';
import { Gateway } from 'types/embedded/gateway';
import { Mapping } from 'types/embedded/gatewayRule';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Permission } from 'types/embedded/permission';

import { GatewayRulesShowChipFields } from './GatewayRulesShowChipFields';
import { GatewayRulesShowMappingRow } from './GatewayRulesShowMappingRow';

export const GatewayRulesShow: React.FC = () => {
  const translate = useTypedTranslate();
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const { createPathWithQuery } = useCreatePathWithQuery();

  const { record, isLoading } = useShowController();

  const commissionModal = useRecordModalById<Commission>({ resource: Resources.Commissions });
  const merchantAccountModal = useRecordModalById<MerchantAccount>({
    resource: Resources.MerchantAccounts,
  });
  const mappingPercentModal = useRecordModal<Mapping>();
  const deleteModal = useRecordModal();
  const gatewayModal = useRecordModalById<Gateway>({ resource: Resources.Gateways });

  const mappings = [...(record?.mappings ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((mapping) => ({ ...mapping, gatewayRule: { name: record?.name } }));

  function renderMappingFields(): JSX.Element | JSX.Element[] {
    return mappings.map((mapping, index) => {
      return (
        <RecordContextProvider key={index} value={mapping}>
          <GatewayRulesShowMappingRow
            mapping={mapping}
            merchantId={record?.merchant?.['@id']}
            onOpenCommission={commissionModal.handleOpenRecordModal}
            onOpenGateway={gatewayModal.handleOpenRecordModal}
            onOpenMerchant={merchantAccountModal.handleOpenRecordModal}
            onOpenPercentEdit={mappingPercentModal.handleOpenRecordModal}
          />
        </RecordContextProvider>
      );
    });
  }

  return (
    <ShowPage
      actions={[
        hasResourcePermissions(Resources.GatewayRules, [Permission.Full, Permission.Update]) && (
          <GatewayRuleActiveSwitch key="gateway-rule-switch" sx={{ margin: 0 }} />
        ),
        hasResourceAccess(Resources.UserActions) && (
          <UserActionsIconButton entityName="gateway_rule" key="gateway-rule-link" />
        ),
      ]}
      listName={translate('app.gatewayRules.titleText')}
      rightActionsSlot={[
        hasResourcePermissions(Resources.GatewayRules, [Permission.Full, Permission.Delete]) && (
          <DeleteButton
            key="gateway-rule-show-delete"
            label={translate('app.gatewayRules.deleteRule')}
            onClick={deleteModal.handleOpenRecordModal}
            variant="outlined"
          />
        ),
        hasResourcePermissions(Resources.GatewayRules, [Permission.Full, Permission.Update]) && (
          <Button
            component={Link}
            key="edit-button"
            label={translate('app.gatewayRules.editRuleBtn')}
            to={createPathWithQuery({
              resource: Resources.GatewayRules,
              id: record?.id,
              type: 'edit',
            })}
            variant="contained"
          />
        ),
      ]}
      title={translate('app.gatewayRules.ruleDetails')}
    >
      <GatewayRulesShowChipFields />
      <>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
            <Box>
              <CardHeader
                sx={{ padding: '24px 0 16px 0' }}
                title={translate('app.gatewayRules.condition')}
                titleSize="smallBold"
              />
              <RecordContextProvider value={record}>
                <GatewayRuleConditionsProvider>
                  <GatewayRuleField label={translate('app.gatewayRules.rules')} sortable={false} />
                </GatewayRuleConditionsProvider>
              </RecordContextProvider>
            </Box>
            <CardHeader
              sx={{ padding: '24px 0 16px 0' }}
              title={translate('app.gatewayRules.mappings')}
              titleSize="smallBold"
            />
            <Box sx={{ marginBottom: '16px' }}>{renderMappingFields()}</Box>
          </>
        )}
        {commissionModal.openRecordModal && commissionModal.record && (
          <CommissionShowDialog
            commission={commissionModal.record}
            onClose={commissionModal.handleCloseRecordModal}
            open={commissionModal.openRecordModal}
          />
        )}
        {merchantAccountModal.openRecordModal && merchantAccountModal.record && (
          <MerchantAccountShowDialog
            merchantAccount={merchantAccountModal.record}
            onClose={merchantAccountModal.handleCloseRecordModal}
            open={merchantAccountModal.openRecordModal}
          />
        )}
        {mappingPercentModal.record && (
          <GatewayRuleMappingPercentEditDialog
            mapping={mappingPercentModal.record}
            onClose={mappingPercentModal.handleCloseRecordModal}
            open={mappingPercentModal.openRecordModal}
          />
        )}
        {deleteModal.openRecordModal && (
          <GatewayRuleModalDelete
            id={record?.id}
            merchantName={record?.merchant?.name}
            onClose={deleteModal.handleCloseRecordModal}
            open={deleteModal.openRecordModal}
          />
        )}
        {gatewayModal.openRecordModal && (
          <GatewayShowDialog
            gateway={gatewayModal.record}
            isFetching={gatewayModal.isFetching}
            onClose={gatewayModal.handleCloseRecordModal}
            open={gatewayModal.openRecordModal}
          />
        )}
      </>
    </ShowPage>
  );
};
